// First-run chain orchestrator — unified Google auth. Runs after every OAuth return.
//
// The sign-in grant now carries the Google calendar tokens (one account, one
// consent). Here we:
//   - if the session has provider tokens → link them as the calendar connection,
//   - else (a returning login with no fresh token) → only if there's NO working
//     connection, force a one-off re-consent to obtain one (R2: prompt=consent is
//     never every-login),
//   - then ensure a silent default schedule → Home (or a stashed invite target).
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { supabase, auth, rememberProvider } from '../lib/supabase';
import { hasCalendarConnected, linkGoogleCalendar, linkMicrosoftCalendar, syncNow } from '../services/calendar';
import { ensureDefaultSchedule } from '../services/schedule';
import { capture } from '../lib/analytics';
import { Screen, Card, Title, Button, Spinner } from '../components/ui';

const NEXT_KEY = 'when_next';

export default function AuthCallback() {
  const { user, loading } = useAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const ran = useRef(false);
  // Which identity provider this session authenticated with ('google' | 'microsoft').
  // Drives which link endpoint and which re-consent sign-in to use. Held in a ref so
  // the error-state retry button uses the same provider.
  const providerRef = useRef('google');
  const [error, setError] = useState(null);

  useEffect(() => {
    if (loading || ran.current) return;
    if (!user) { navigate('/', { replace: true }); return; }
    ran.current = true;

    const nextParam = params.get('next');
    if (nextParam) sessionStorage.setItem(NEXT_KEY, nextParam);
    // Guard against a repair loop: we only force-consent once.
    const alreadyReconnecting = params.get('reconnect') === '1';

    const resume = () => {
      const next = sessionStorage.getItem(NEXT_KEY);
      sessionStorage.removeItem(NEXT_KEY);
      navigate(next || '/home', { replace: true });
    };

    const forceReconnect = async () => {
      const next = sessionStorage.getItem(NEXT_KEY);
      const nextQ = next ? `&next=${encodeURIComponent(next)}` : '';
      await auth.signInWith(providerRef.current, {
        redirectTo: `${window.location.origin}/auth/callback?reconnect=1${nextQ}`,
        forceConsent: true,
      });
    };

    (async () => {
      try {
        // The sign-in grant carries the calendar tokens in the session. Which
        // provider it was (Google, Microsoft/Azure, or email) selects the link
        // endpoint. The provider comes from the `provider=` param that
        // auth.signInWith* stamped on redirectTo. app_metadata.provider is only a
        // fallback: it is the user's FIRST provider, not this session's, so for a
        // linked-identity user it names the wrong one (see lib/supabase.js).
        const { data: { session } } = await supabase.auth.getSession();
        const rawProvider = params.get('provider') || session?.user?.app_metadata?.provider;
        const providerName = (rawProvider === 'azure' || rawProvider === 'microsoft') ? 'microsoft'
          : rawProvider === 'email' ? 'email' : 'google';
        providerRef.current = providerName;
        rememberProvider(providerName);

        // Email magic-link identity grants NO calendar (no provider_token). Send the
        // user to the iCloud connect screen if they have no calendar yet; otherwise
        // resume. Never run the OAuth link or the force-consent repair loop here.
        if (providerName === 'email') {
          const connected = await hasCalendarConnected();
          if (!connected) { navigate('/connect', { replace: true }); return; }
          await ensureDefaultSchedule();
          resume();
          return;
        }

        const linkCalendar = providerName === 'microsoft' ? linkMicrosoftCalendar : linkGoogleCalendar;
        const providerToken = session?.provider_token;
        const providerRefresh = session?.provider_refresh_token;

        if (providerToken) {
          // One-grant path: link the calendar from the sign-in tokens. The calendar
          // is guaranteed to be the signed-in account — no divergence possible.
          try {
            await linkCalendar({ accessToken: providerToken, refreshToken: providerRefresh });
          } catch (err) {
            // Existing-user migration: the login returned a cached grant without
            // calendar.events. The link was rejected (nothing stored). Force ONE fresh
            // consent to obtain the scope, then link succeeds on the return.
            const needsConsent = err?.status === 422 && (err?.body?.needs_consent || err?.detail === 'insufficient_scope');
            if (needsConsent && !alreadyReconnecting) {
              await forceReconnect();
              return; // navigating away to Google (prompt=consent)
            }
            throw err;
          }
          capture('calendar_connected', { provider: providerName });
          syncNow().catch(() => {}); // fresh busy data for the first overlap; don't block
        } else {
          // Returning login with no fresh token. Repair ONLY if nothing is connected.
          const connected = await hasCalendarConnected();
          if (!connected) {
            if (alreadyReconnecting) {
              setError('We couldn\'t connect your calendar. Try again.');
              return;
            }
            await forceReconnect();
            return; // navigating away to Google (with prompt=consent)
          }
          // else: an existing working connection — nothing to do, no re-consent.
        }

        await ensureDefaultSchedule();
        resume();
      } catch (err) {
        console.error('First-run chain failed:', err);
        setError('Something went wrong finishing setup. Try again.');
      }
    })();
  }, [loading, user, params, navigate]);

  const retry = async () => {
    setError(null);
    try {
      const next = sessionStorage.getItem(NEXT_KEY);
      const nextQ = next ? `&next=${encodeURIComponent(next)}` : '';
      await auth.signInWith(providerRef.current, {
        redirectTo: `${window.location.origin}/auth/callback?reconnect=1${nextQ}`,
        forceConsent: true,
      });
    } catch {
      setError('Couldn\'t start calendar connect. Try again.');
    }
  };

  if (error) {
    return (
      <Screen>
        <Card>
          <Title className="text-2xl">Almost there</Title>
          <p className="mt-4 font-sans text-base text-ink/70">{error}</p>
          <div className="mt-8"><Button onClick={retry}>Connect calendar</Button></div>
        </Card>
      </Screen>
    );
  }

  return <Screen><Spinner label="Setting things up" /></Screen>;
}
